import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface CounterItem {
  value: number;
  suffix: string;
  labelKey: string;
  icon: string;
}

const counters: CounterItem[] = [
  { value: 6, suffix: '', labelKey: 'numbers.courts', icon: 'ri-flag-line' },
  { value: 14, suffix: 'h', labelKey: 'numbers.hours', icon: 'ri-time-line' },
  { value: 300, suffix: '', labelKey: 'numbers.members', icon: 'ri-team-line' },
  { value: 2, suffix: '', labelKey: 'numbers.itf', icon: 'ri-trophy-line' },
  { value: 8, suffix: '+', labelKey: 'numbers.coaches', icon: 'ri-user-star-line' },
  { value: 60, suffix: '+', labelKey: 'numbers.camp', icon: 'ri-heart-pulse-line' },
];

function AnimatedCounter({ target, suffix }: { target: number; suffix: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  const animate = useCallback(() => {
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      current = Math.min(Math.round(increment * step), target);
      setCount(current);
      if (step >= steps) {
        clearInterval(timer);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [target]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          animate();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [animate]);

  return (
    <span ref={ref} className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary-600">
      {count}{suffix}
    </span>
  );
}

export default function KeyNumbers() {
  const { t } = useTranslation();

  return (
    <section className="relative py-16 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-6 md:gap-8">
            {counters.map((item) => (
              <div
                key={item.labelKey}
                className="flex flex-col items-center text-center p-6 rounded-2xl bg-background-100/70 border border-background-200/60 transition-all duration-300 hover:border-accent-300/40 hover:bg-background-100"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className={`${item.icon} text-lg md:text-xl`}></i>
                </div>
                <AnimatedCounter target={item.value} suffix={item.suffix} />
                <p className="text-xs md:text-sm text-foreground-600 mt-2 leading-tight max-w-[140px]">
                  {t(item.labelKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}